import React, { useEffect, useRef, useState } from "react";
import Drawer from "react-modern-drawer";
import { FaAngleDoubleRight, FaPhone, FaEnvelope, FaQuestionCircle, FaSearch } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { IoSend } from "react-icons/io5";
import { MdSupportAgent, MdEmail, MdPhone } from "react-icons/md";
import { BiSupport } from "react-icons/bi";
import * as timeago from "timeago.js";
import { useQueryClient } from "@tanstack/react-query";
import { useGetFaqs } from "../../api/services/faqServices";
import { useCreateSupportTicket, useGetSupportTickets, useGetSupportTicket } from "../../api/services/supportTicketServices";
import NoData from "../../components/common/NoData";
import { useSelector } from "react-redux";
import { errorToast, successToast } from "../../helpers/constants";

const Help = ({ isHelpOpen, toggleHelp }) => {
  const { data: faqs } = useGetFaqs();
  const queryClient = useQueryClient();
  const { data: supportTicketsData, isLoading: supportTicketsLoading } = useGetSupportTickets({
    page: 1,
    limit: 5,
  });
  const createSupportTicket = useCreateSupportTicket();
  const { user } = useSelector((state) => state.auth);

  const [openIndex, setOpenIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [isTicketDrawerOpen, setIsTicketDrawerOpen] = useState(false);
  const [supportForm, setSupportForm] = useState({
    name: user?.fullname || user?.fullName || `${user?.firstname || ""} ${user?.lastname || ""}`.trim(),
    email: user?.email || "",
    subject: "",
    description: "",
    priority: "medium",
    category: "",
  });

  const quickIssues = [
    { label: "Payment Stuck", category: "payments", subject: "Payment issue" },
    { label: "Event Setup", category: "events", subject: "Event setup help" },
    { label: "Login Problem", category: "account", subject: "Login access issue" },
    { label: "Technical Bug", category: "technical", subject: "Technical issue" },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleFormChange = (e) => {
    setSupportForm({ ...supportForm, [e.target.name]: e.target.value });
  };

  const handleSubmitSupport = (e) => {
    e.preventDefault();
    const payload = {
      subject: supportForm.subject,
      description: supportForm.description,
      category: supportForm.category,
      priority: supportForm.priority,
    };

    createSupportTicket.mutate(payload, {
      onSuccess: (data) => {
        successToast(data?.message || "Support ticket created successfully");
        setSupportForm((prev) => ({
          ...prev,
          subject: "",
          description: "",
          category: "",
          priority: "medium",
        }));
        queryClient.invalidateQueries({ queryKey: ["getSupportTickets"] });
      },
      onError: (err) => {
        errorToast(err?.response?.data?.message || "Failed to create support ticket");
      },
    });
  };

  const handleQuickPick = (issue) => {
    setSupportForm((prev) => ({
      ...prev,
      category: issue.category,
      subject: issue.subject,
    }));
  };

  const filteredFaqs = faqs?.data?.filter((faq) =>
    faq.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.details.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const supportTickets = supportTicketsData?.data || [];
  const pendingCount = supportTickets.filter((ticket) => ticket.status === "pending").length;
  const resolvedCount = supportTickets.filter((ticket) => ticket.status === "resolved").length;
  const totalTickets = supportTicketsData?.pagination?.total ?? supportTickets.length;

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const { data: supportTicketDetailsData, isLoading: supportTicketDetailsLoading } = useGetSupportTicket(
    selectedTicketId,
    isTicketDrawerOpen
  );

  const ticketDetails = supportTicketDetailsData?.data;

  const scrollRef = useRef();
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    setSupportForm((prev) => ({
      ...prev,
      name: user?.fullname || user?.fullName || `${user?.firstname || ""} ${user?.lastname || ""}`.trim(),
      email: user?.email || "",
    }));
  }, [user?.fullname, user?.fullName, user?.firstname, user?.lastname, user?.email]);

  const handleOpenTicket = (ticketId) => {
    setSelectedTicketId(ticketId);
    setIsTicketDrawerOpen(true);
  };

  const handleCloseTicket = () => {
    setIsTicketDrawerOpen(false);
    setSelectedTicketId(null);
  };

  return (
    <Drawer
      open={isHelpOpen}
      onClose={toggleHelp}
      direction="right"
      className="p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-l-md bg-gray-50"
      size={typeof window !== 'undefined' && window.innerWidth < 640 ? '90vw' : window.innerWidth < 768 ? 400 : 480}
    >
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <div className="flex items-center gap-2">
          <BiSupport className="text-xl sm:text-2xl text-primary" />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Help & Support</h2>
        </div>
        <FaAngleDoubleRight
          onClick={toggleHelp}
          className="cursor-pointer text-gray-600 hover:text-primary transition-colors text-lg sm:text-xl"
        />
      </div>

      <div className="grid grid-cols-4 gap-1.5 sm:gap-2 mb-4 sm:mb-6">
        <button
          onClick={() => setActiveTab("overview")}
          className={`p-2 sm:p-2.5 text-center text-[0.65rem] sm:text-xs font-semibold rounded-lg transition-all duration-200 ${
            activeTab === "overview"
              ? "bg-primary text-white shadow-md"
              : "bg-white text-gray-700 hover:bg-gray-100"
          }`}
        >
          <MdSupportAgent className="mx-auto text-lg sm:text-xl mb-0.5 sm:mb-1" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab("faq")}
          className={`p-2 sm:p-2.5 text-center text-[0.65rem] sm:text-xs font-semibold rounded-lg transition-all duration-200 ${
            activeTab === "faq"
              ? "bg-primary text-white shadow-md"
              : "bg-white text-gray-700 hover:bg-gray-100"
          }`}
        >
          <FaQuestionCircle className="mx-auto text-lg sm:text-xl mb-0.5 sm:mb-1" />
          FAQs
        </button>
        <button
          onClick={() => setActiveTab("contact")}
          className={`p-2 sm:p-2.5 text-center text-[0.65rem] sm:text-xs font-semibold rounded-lg transition-all duration-200 ${
            activeTab === "contact"
              ? "bg-primary text-white shadow-md"
              : "bg-white text-gray-700 hover:bg-gray-100"
          }`}
        >
          <FaEnvelope className="mx-auto text-lg sm:text-xl mb-0.5 sm:mb-1" />
          Contact
        </button>
        <button
          onClick={() => setActiveTab("tickets")}
          className={`p-2 sm:p-2.5 text-center text-[0.65rem] sm:text-xs font-semibold rounded-lg transition-all duration-200 ${
            activeTab === "tickets"
              ? "bg-primary text-white shadow-md"
              : "bg-white text-gray-700 hover:bg-gray-100"
          }`}
        >
          <BiSupport className="mx-auto text-lg sm:text-xl mb-0.5 sm:mb-1" />
          Tickets
        </button>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === "overview" && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-gradient-to-r from-primary to-secondary text-white p-4 sm:p-6 rounded-xl mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2">Need Help?</h3>
              <p className="text-xs sm:text-sm text-blue-100">
                We're here to assist you with any questions or issues you may have.
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="bg-green-100 p-2 sm:p-3 rounded-full flex-shrink-0">
                    <FaPhone className="text-green-600 text-base sm:text-xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">Call Us</h4>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2">
                      Speak with our support team
                    </p>
                    <div className="space-y-1.5 sm:space-y-2">
                      <a
                        href="tel:+2202600001"
                        className="text-primary font-bold text-base sm:text-lg hover:underline block break-all"
                      >
                        +220 260 0001
                      </a>
                      <a
                        href="tel:1666"
                        className="text-green-600 font-bold text-base sm:text-lg hover:underline block"
                      >
                        1666 (Toll-Free)
                      </a>
                    </div>
                    <p className="text-[0.65rem] sm:text-xs text-gray-500 mt-1">
                      Available 24/7
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-full flex-shrink-0">
                    <FaEnvelope className="text-blue-600 text-base sm:text-xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">Email Support</h4>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2">
                      Send us a detailed message
                    </p>
                    <a
                      href="mailto:support@zappme.net"
                      className="text-primary font-semibold hover:underline text-sm sm:text-base break-all"
                    >
                      support@zappme.net
                    </a>
                    <p className="text-[0.65rem] sm:text-xs text-gray-500 mt-1">
                      We'll respond within 24 hours
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="bg-purple-100 p-3 rounded-full">
                    <FaQuestionCircle className="text-purple-600 text-xl" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">Browse FAQs</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Find answers to common questions
                    </p>
                    <button
                      onClick={() => setActiveTab("faq")}
                      className="text-primary font-semibold hover:underline text-sm"
                    >
                      View All FAQs →
                    </button>
                  </div>
                </div>
              </div>

              {/* <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="bg-orange-100 p-3 rounded-full">
                    <FaBook className="text-orange-600 text-xl" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">Documentation</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Detailed guides and tutorials
                    </p>
                    <a
                      href="#"
                      className="text-primary font-semibold hover:underline text-sm"
                    >
                      Read Documentation →
                    </a>
                  </div>
                </div>
              </div> */}
            </div>

            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Quick Tip:</strong> Before contacting support, check our FAQs section. You might find your answer instantly!
              </p>
            </div>
          </motion.div>
        )}

        {activeTab === "faq" && (
          <motion.div
            key="faq"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {faqs?.data?.length > 0 && (
              <div className="mb-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search FAQs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  />
                </div>
              </div>
            )}

            {filteredFaqs?.length === 0 ? (
              <NoData text={searchQuery ? "No FAQs match your search" : "No FAQs Added"} className={"mt-10"} />
            ) : (
              <div className="space-y-3">
                {filteredFaqs?.map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="flex justify-between items-center w-full text-left p-4 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm font-semibold text-gray-800 pr-2">
                        {faq.title}
                      </span>
                      <span className="text-primary text-xl font-bold flex-shrink-0">
                        {openIndex === index ? "−" : "+"}
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {openIndex === index && (
                        <motion.div
                          key="content"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-2 text-sm text-gray-600 bg-gray-50 border-t border-gray-200">
                            {faq.details}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "contact" && (
          <motion.div
            key="contact"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="space-y-4">
              <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                  <MdPhone className="text-green-600 text-xl" />
                  Prefer to Call?
                </h3>
                <div className="flex items-center gap-2">
                  <a
                    href="tel:+2202600001"
                    className="block w-full bg-primary hover:bg-primary/90 text-white text-center py-2 rounded-lg font-semibold text-xs transition-colors duration-200"
                  >
                    📞 Call +220 260 0001
                  </a>
                  <a
                    href="tel:1666"
                    className="block w-full bg-green-500 hover:bg-green-600 text-white text-center py-2 rounded-lg font-semibold text-xs transition-colors duration-200"
                  >
                    📞 Call 1666 (Toll-Free)
                  </a>
                </div>
                <p className="text-[0.6rem] text-center text-gray-500 mt-2">
                  Available 24/7 - We're always here to help
                </p>
              </div>

              <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <MdEmail className="text-primary text-xl" />
                  Submit a Support Request
                </h3>

                <div className="mb-4 rounded-lg border border-blue-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 p-3">
                  <p className="text-xs text-gray-700">
                    Share the essentials and we will triage your request fast. Tickets are tracked and prioritized based on impact.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {quickIssues.map((issue) => (
                      <button
                        key={issue.label}
                        type="button"
                        onClick={() => handleQuickPick(issue)}
                        className="rounded-full border border-blue-200 bg-white px-3 py-1 text-[0.65rem] font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-50"
                      >
                        {issue.label}
                      </button>
                    ))}
                  </div>
                </div>

                <form onSubmit={handleSubmitSupport} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={supportForm.name}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={supportForm.email}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    name="category"
                    value={supportForm.category}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  >
                    <option value="">Select a category</option>
                    <option value="account">Account & Login</option>
                    <option value="payments">Payments & Billing</option>
                    <option value="products">Products & Inventory</option>
                    <option value="events">Events & Bookings</option>
                    <option value="technical">Technical Issues</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority Level
                  </label>
                  <select
                    name="priority"
                    value={supportForm.priority}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  >
                    <option value="low">Low - General inquiry</option>
                    <option value="medium">Medium - Need assistance</option>
                    <option value="high">High - Urgent issue</option>
                    <option value="urgent">Urgent - Immediate attention</option>
                    <option value="critical">Critical - Business stopped</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={supportForm.subject}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="Brief description of your issue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={supportForm.description}
                    onChange={handleFormChange}
                    required
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm resize-none"
                    placeholder="Please provide detailed information about your issue..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={createSupportTicket.isLoading}
                  className="w-full bg-primary hover:bg-primary/90 disabled:opacity-70 text-white py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <IoSend />
                  {createSupportTicket.isLoading ? "Sending..." : "Submit Support Request"}
                </button>
              </form>

                <p className="text-xs text-gray-500 text-center mt-4">
                  We typically respond within 24 hours during business days
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "tickets" && (
          <motion.div
            key="tickets"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 text-white p-4 rounded-xl mb-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold">Support Tickets</h3>
                  <p className="text-xs text-slate-200">
                    Track updates
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="min-w-[68px] flex-1 rounded-lg bg-white/10 px-2 py-1 text-center">
                    <p className="text-[0.55rem] text-slate-200 uppercase tracking-wide">Total</p>
                    <p className="text-xs font-semibold text-white">{totalTickets}</p>
                  </div>
                  <div className="min-w-[68px] flex-1 rounded-lg bg-amber-400/20 px-2 py-1 text-center">
                    <p className="text-[0.55rem] text-amber-100 uppercase tracking-wide">Pending</p>
                    <p className="text-xs font-semibold text-amber-100">{pendingCount}</p>
                  </div>
                  <div className="min-w-[68px] flex-1 rounded-lg bg-emerald-400/20 px-2 py-1 text-center">
                    <p className="text-[0.55rem] text-emerald-100 uppercase tracking-wide">Resolved</p>
                    <p className="text-xs font-semibold text-emerald-100">{resolvedCount}</p>
                  </div>
                </div>
              </div>
            </div>

            {supportTicketsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="h-16 rounded-lg bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : supportTickets.length ? (
              <div className="space-y-3 min-w-0">
                {supportTickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    type="button"
                    onClick={() => handleOpenTicket(ticket.id)}
                    className="w-full text-left bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:border-primary/40 hover:shadow-md transition"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[0.65rem] font-semibold text-gray-500">{ticket.ticketId || "Ticket"}</span>
                          <span className="text-[0.6rem] text-gray-400">•</span>
                          <span className="text-[0.65rem] text-gray-500">
                            {ticket.category ? ticket.category.replace(/_/g, " ") : "General"}
                          </span>
                        </div>
                        <h4 className="text-sm font-semibold text-gray-800 truncate">
                          {ticket.subject}
                        </h4>
                        <p className="text-[0.7rem] text-gray-500 mt-1 line-clamp-2">
                          {ticket.description}
                        </p>
                        <div className="mt-2 flex items-center gap-2 text-[0.6rem] text-gray-400">
                          <span>Updated {formatDateTime(ticket.updatedAt || ticket.createdAt)}</span>
                          <span className="text-gray-300">|</span>
                          <span className="text-primary font-semibold">View details</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span
                          className={`text-[0.65rem] font-semibold px-2 py-1 rounded-full ${
                            ticket.status === "resolved"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {ticket.status === "resolved" ? "Resolved" : "Pending"}
                        </span>
                        <span
                          className={`text-[0.6rem] font-semibold px-2 py-1 rounded-full ${
                            ticket.priority === "critical" || ticket.priority === "urgent"
                              ? "bg-red-100 text-red-700"
                              : ticket.priority === "high"
                              ? "bg-orange-100 text-orange-700"
                              : ticket.priority === "medium"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {ticket.priority}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                No tickets yet. Your submissions will appear here.
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <Drawer
        open={isTicketDrawerOpen}
        onClose={handleCloseTicket}
        direction="right"
        size={Math.min(420, window.innerWidth)}
        className="p-0"
        style={{ maxWidth: "100vw" }}
      >
        <div className="flex flex-col h-full bg-white">
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
            <div>
              <p className="text-[0.65rem] text-gray-500">Ticket details</p>
              <h3 className="text-base font-semibold text-gray-800">
                {ticketDetails?.ticketId || "Support Ticket"}
              </h3>
              <p className="text-xs text-gray-500">{ticketDetails?.subject || ""}</p>
            </div>
            <button
              onClick={handleCloseTicket}
              className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50"
            >
              Close
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {supportTicketDetailsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-14 rounded-lg bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-gray-200 p-3">
                    <p className="text-[0.6rem] text-gray-500">Status</p>
                    <p className="text-sm font-semibold text-gray-800 capitalize">
                      {ticketDetails?.status || "pending"}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3">
                    <p className="text-[0.6rem] text-gray-500">Priority</p>
                    <p className="text-sm font-semibold text-gray-800 capitalize">
                      {ticketDetails?.priority || "medium"}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3">
                    <p className="text-[0.6rem] text-gray-500">Category</p>
                    <p className="text-sm font-semibold text-gray-800 capitalize">
                      {ticketDetails?.category ? ticketDetails.category.replace(/_/g, " ") : "General"}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3">
                    <p className="text-[0.6rem] text-gray-500">Type</p>
                    <p className="text-sm font-semibold text-gray-800 capitalize">
                      {ticketDetails?.ticketType ? ticketDetails.ticketType.replace(/_/g, " ") : "Support"}
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-[0.6rem] text-gray-500">Description</p>
                  <p className="text-sm text-gray-700 mt-1">
                    {ticketDetails?.description || "No description provided."}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-gray-200 p-3">
                    <p className="text-[0.6rem] text-gray-500">Created</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {formatDateTime(ticketDetails?.createdAt)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3">
                    <p className="text-[0.6rem] text-gray-500">Updated</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {formatDateTime(ticketDetails?.updatedAt)}
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-[0.6rem] text-gray-500">Resolution Notes</p>
                  <p className="text-sm text-gray-700 mt-1">
                    {ticketDetails?.resolutionNotes || "No resolution notes yet."}
                  </p>
                </div>

                <div className="rounded-lg border border-dashed border-gray-200 p-3">
                  <p className="text-[0.6rem] text-gray-500">Comments</p>
                  <p className="text-xs text-gray-600 mt-2">
                    Comments are not available yet. Once enabled, you will see updates and can reply here.
                  </p>
                  <textarea
                    rows={3}
                    disabled
                    placeholder="Commenting will be enabled soon"
                    className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-500 bg-gray-50 resize-none"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Drawer>
    </Drawer>
  );
};

export default Help;
